/**
 * Lightweight pre-upload photo editor: rotate, crop, brightness, contrast, saturation, warmth.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  RotateLeft,
  RotateRight,
  RestartAlt,
} from '@mui/icons-material';
import { IMAGE_EDIT_DEFAULTS } from '../../utils/applyImageEdits';
import { buildColorCssFilter } from '../../utils/mediaColorFilter';

const ASPECT_OPTIONS = [
  { value: 'free', label: 'Free', aspect: null },
  { value: '1:1', label: '1:1', aspect: 1 },
  { value: '4:3', label: '4:3', aspect: 4 / 3 },
  { value: '16:9', label: '16:9', aspect: 16 / 9 },
];

function aspectKeyFromValue(aspect) {
  if (aspect == null) return 'free';
  if (Math.abs(aspect - 1) < 0.001) return '1:1';
  if (Math.abs(aspect - 4 / 3) < 0.001) return '4:3';
  if (Math.abs(aspect - 16 / 9) < 0.001) return '16:9';
  return 'free';
}

export default function PostImageEditor({
  open,
  sourceUrl,
  onClose,
  onApply,
  onSkip,
  applying = false,
}) {
  const [rotation, setRotation] = useState(IMAGE_EDIT_DEFAULTS.rotation);
  const [brightness, setBrightness] = useState(IMAGE_EDIT_DEFAULTS.brightness);
  const [contrast, setContrast] = useState(IMAGE_EDIT_DEFAULTS.contrast);
  const [saturation, setSaturation] = useState(IMAGE_EDIT_DEFAULTS.saturation);
  const [warmth, setWarmth] = useState(IMAGE_EDIT_DEFAULTS.warmth);
  const [aspectKey, setAspectKey] = useState('free');
  const [zoom, setZoom] = useState(IMAGE_EDIT_DEFAULTS.zoom);

  useEffect(() => {
    if (!open) return;
    setRotation(IMAGE_EDIT_DEFAULTS.rotation);
    setBrightness(IMAGE_EDIT_DEFAULTS.brightness);
    setContrast(IMAGE_EDIT_DEFAULTS.contrast);
    setSaturation(IMAGE_EDIT_DEFAULTS.saturation);
    setWarmth(IMAGE_EDIT_DEFAULTS.warmth);
    setAspectKey('free');
    setZoom(IMAGE_EDIT_DEFAULTS.zoom);
  }, [open, sourceUrl]);

  const aspect = useMemo(
    () => ASPECT_OPTIONS.find((o) => o.value === aspectKey)?.aspect ?? null,
    [aspectKey]
  );

  const previewFilter = buildColorCssFilter({ brightness, contrast, saturation, warmth });
  const previewTransform = `rotate(${rotation}deg) scale(${zoom})`;

  const reset = () => {
    setRotation(0);
    setBrightness(0);
    setContrast(0);
    setSaturation(0);
    setWarmth(0);
    setAspectKey('free');
    setZoom(1);
  };

  const handleApply = () => {
    onApply?.({
      rotation,
      brightness,
      contrast,
      saturation,
      warmth,
      aspect,
      zoom,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={applying ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      data-testid="post-image-editor"
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Adjust photo</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'grey.900',
            minHeight: 260,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          {/* Aspect guide overlay */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                width: aspect == null ? '92%' : aspect >= 1 ? '88%' : undefined,
                height: aspect == null ? '92%' : aspect < 1 ? '88%' : undefined,
                aspectRatio: aspect == null ? undefined : String(aspect),
                maxWidth: '92%',
                maxHeight: '92%',
                border: '2px solid',
                borderColor: 'common.white',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
                borderRadius: 1,
              }}
            />
          </Box>
          {sourceUrl && (
            <Box
              component="img"
              src={sourceUrl}
              alt="Edit preview"
              sx={{
                maxWidth: '100%',
                maxHeight: 320,
                objectFit: 'contain',
                filter: previewFilter,
                transform: previewTransform,
                transition: 'transform 120ms ease, filter 120ms ease',
              }}
            />
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Rotate left">
              <IconButton
                size="small"
                aria-label="Rotate left"
                onClick={() => setRotation((r) => (r + 270) % 360)}
                disabled={applying}
              >
                <RotateLeft fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rotate right">
              <IconButton
                size="small"
                aria-label="Rotate right"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                disabled={applying}
              >
                <RotateRight fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset">
              <IconButton size="small" aria-label="Reset adjustments" onClick={reset} disabled={applying}>
                <RestartAlt fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {rotation}°
          </Typography>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.04, textTransform: 'uppercase' }}>
          Crop
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={aspectKey}
          onChange={(_e, next) => {
            if (next) setAspectKey(next);
          }}
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, my: 1 }}
          disabled={applying}
        >
          {ASPECT_OPTIONS.map((opt) => (
            <ToggleButton
              key={opt.value}
              value={opt.value}
              sx={{ textTransform: 'none', px: 1.25, flex: '1 1 auto' }}
            >
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Typography variant="caption" color="text.secondary">Zoom</Typography>
        <Slider
          size="small"
          min={1}
          max={2.5}
          step={0.05}
          value={zoom}
          onChange={(_e, v) => setZoom(v)}
          disabled={applying}
          valueLabelDisplay="auto"
          aria-label="Crop zoom"
          sx={{ mb: 1.5, mt: 0.5 }}
        />

        <Typography variant="caption" color="text.secondary">Brightness</Typography>
        <Slider
          size="small"
          min={-40}
          max={40}
          step={1}
          value={brightness}
          onChange={(_e, v) => setBrightness(v)}
          disabled={applying}
          valueLabelDisplay="auto"
          aria-label="Brightness"
          sx={{ mb: 1.5, mt: 0.5 }}
        />

        <Typography variant="caption" color="text.secondary">Contrast</Typography>
        <Slider
          size="small"
          min={-40}
          max={40}
          step={1}
          value={contrast}
          onChange={(_e, v) => setContrast(v)}
          disabled={applying}
          valueLabelDisplay="auto"
          aria-label="Contrast"
          sx={{ mb: 1.5, mt: 0.5 }}
        />

        <Typography variant="caption" color="text.secondary">Saturation</Typography>
        <Slider
          size="small"
          min={-40}
          max={40}
          step={1}
          value={saturation}
          onChange={(_e, v) => setSaturation(v)}
          disabled={applying}
          valueLabelDisplay="auto"
          aria-label="Saturation"
          data-testid="post-image-saturation"
          sx={{ mb: 1.5, mt: 0.5 }}
        />

        <Typography variant="caption" color="text.secondary">Warmth</Typography>
        <Slider
          size="small"
          min={-40}
          max={40}
          step={1}
          value={warmth}
          onChange={(_e, v) => setWarmth(v)}
          disabled={applying}
          valueLabelDisplay="auto"
          aria-label="Warmth"
          data-testid="post-image-warmth"
          sx={{ mt: 0.5 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={applying} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        {onSkip && (
          <Button onClick={onSkip} disabled={applying || !sourceUrl} sx={{ textTransform: 'none' }}>
            Skip editing
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={applying || !sourceUrl}
          sx={{ textTransform: 'none', fontWeight: 700 }}
          data-testid="post-image-editor-apply"
        >
          {applying ? 'Applying…' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { aspectKeyFromValue, ASPECT_OPTIONS };
